import { Injectable } from '@angular/core';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { EMPTY, Observable, throwError } from 'rxjs';
import { catchError, finalize, tap } from 'rxjs/operators';
import { NgxSpinnerService } from 'ngx-spinner';
import { ServerResponse } from '../models/ServerResponse';

@Injectable({
  providedIn: 'root'
})
export class GenericService {
  private requestCount = 0;

  constructor(
    private _http: HttpClient,
    private _spinnerService: NgxSpinnerService
  ) { }

  private handleErrors(err: HttpErrorResponse): Observable<never> {
    // Always hide spinner on error
    this.hideSpinner();

    // Handle canceled requests silently (optional: log or report)
    if (err.status === 0 && err.error instanceof ProgressEvent) {
      console.warn('HTTP request canceled by client:', err);
      return EMPTY; // RxJS EMPTY observable — completes silently
    }

    const errorMessage = err.error instanceof Error ? err.error.message : null;

    const errorMessages: { [status: number]: string } = {
      400: 'Bad Request',
      405: 'Method Not Allowed',
      500: 'Internal Server Error',
    };

    const warningMessages: { [status: number]: string } = {
      401: 'Unauthorized',
      404: 'Not Found',
      409: 'Conflict',
    };

    const customErrorMessage =
      errorMessage ||
      errorMessages[err.status] ||
      warningMessages[err.status] ||
      'An unexpected error occurred.';

    const isWarning = warningMessages.hasOwnProperty(err.status);

    // Display a snackbar or notification (uncomment if needed)
    // this._snackbar.callSnackBar(
    //   customErrorMessage,
    //   isWarning ? SnackBarType.WARNING : SnackBarType.ERROR
    // );

    return throwError(() => err);
  }


  private showSpinner() {
    if (this.requestCount === 0) {
      this._spinnerService.show();
    }
    this.requestCount++;
  }

  private hideSpinner() {
    this.requestCount--;
    if (this.requestCount === 0) {
      this._spinnerService.hide();
    }
  }

  /**
 * Helper to check if an HTTP error is due to cancellation
 */
  private isCanceledRequest(error: any): boolean {
    return error instanceof HttpErrorResponse &&
      error.status === 0 &&
      error.error instanceof ProgressEvent;
  }

  /**
 * Generic GET request
 */
  get<T>(url: string, callLoader: boolean = true, params?: any): Observable<ServerResponse<T>> {
    if (callLoader) this.showSpinner();

    return this._http.get<ServerResponse<T>>(url, { params }).pipe(
      catchError(err => this.handleErrors(err)),
      finalize(() => { if (callLoader) this.hideSpinner(); })
    );
  }

  post(url: string, body: any, options?: any, callLoader: boolean = true): Observable<any> {
    if (callLoader) this.showSpinner();

    return this._http.post(url, body, options).pipe(
      catchError(err => this.handleErrors(err)),
      finalize(() => { if (callLoader) this.hideSpinner(); })
    );
  }

  put(url: string, body: any, options?: any, callLoader: boolean = true): Observable<any> {
    if (callLoader) this.showSpinner();

    return this._http.put(url, body, options).pipe(
      catchError(err => this.handleErrors(err)),
      finalize(() => { if (callLoader) this.hideSpinner(); })
    );
  }

  patch(url: string, body: any, options?: any, callLoader: boolean = true): Observable<any> {
    if (callLoader) this.showSpinner();

    return this._http.patch(url, body, options).pipe(
      catchError(err => this.handleErrors(err)),
      finalize(() => { if (callLoader) this.hideSpinner(); })
    );
  }

  delete<T>(url: string, callLoader: boolean = true): Observable<ServerResponse<T>> {
    if (callLoader) this.showSpinner();

    return this._http.delete<ServerResponse<T>>(url).pipe(
      catchError(err => this.handleErrors(err)),
      finalize(() => { if (callLoader) this.hideSpinner(); })
    );
  }
}
